import { useCallback, useLayoutEffect, useRef } from "react";

export type ReorderAnimationId = number | string;

export function useReorderAnimation(itemIds: ReorderAnimationId[]) {
    const itemRefs = useRef(new Map<ReorderAnimationId, HTMLDivElement>());
    const previousRects = useRef(new Map<ReorderAnimationId, DOMRect>());
    const itemKey = itemIds.join("|");

    const setItemRef = useCallback((id: ReorderAnimationId) => {
        return (node: HTMLDivElement | null) => {
            if (node) {
                itemRefs.current.set(id, node);
                return;
            }

            itemRefs.current.delete(id);
        };
    }, []);

    const capturePositions = useCallback(() => {
        const rects = new Map<ReorderAnimationId, DOMRect>();
        itemRefs.current.forEach((node, id) => {
            rects.set(id, node.getBoundingClientRect());
        });
        previousRects.current = rects;
    }, []);

    useLayoutEffect(() => {
        const rects = previousRects.current;
        if (rects.size === 0) {
            return;
        }

        previousRects.current = new Map();
        const timeouts: number[] = [];

        itemRefs.current.forEach((node, id) => {
            const previousRect = rects.get(id);
            if (!previousRect) {
                return;
            }

            const nextRect = node.getBoundingClientRect();
            const deltaX = previousRect.left - nextRect.left;
            const deltaY = previousRect.top - nextRect.top;

            if (deltaX === 0 && deltaY === 0) {
                return;
            }

            node.style.transition = "none";
            node.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
            void node.offsetHeight;

            requestAnimationFrame(() => {
                node.style.transition = "transform 180ms ease";
                node.style.transform = "";
            });

            timeouts.push(
                window.setTimeout(() => {
                    node.style.transition = "";
                }, 220),
            );
        });

        return () => {
            timeouts.forEach((timeout) => window.clearTimeout(timeout));
        };
    }, [itemKey]);

    return { capturePositions, setItemRef };
}
